import { useCallback, useRef, useState } from 'react'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { MesasDeRonda } from '../componentes/MesasDeRonda.js'
import { PastillaVitrina } from '../componentes/Pastilla.js'
import { TablaPosiciones } from '../componentes/TablaPosiciones.js'
import {
  MODALIDAD,
  MesasDestacadas,
  PodioFinal,
  Punteros,
} from '../componentes/Vitrina.js'
import { useCarga } from '../carga.js'
import { useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import { obtenerRepositorio } from '../datos/index.js'
import type {
  PosicionEquipoPublica,
  PosicionPublica,
  RondaPublica,
  TorneoPublico,
} from '../datos/index.js'
import { cuantosCompiten, fechaLarga } from '../formato.js'
import { ir, reemplazar, vistaPropia } from '../navegacion.js'
import type { PestanaPublica } from '../navegacion.js'
import { MarcadorMatch } from '../componentes/MarcadorMatch.js'
import { Avance } from './Torneo.js'

/**
 * Un torneo mirado desde afuera, sin cuenta.
 *
 * Es la pantalla del torneo propio sin las dos pestañas que dicen «mi»: queda
 * qué es y cómo va, qué pasa en las mesas, y la tabla. Se entra por el resumen y
 * no por las mesas, al revés que el torneo propio, porque el que llega acá no
 * viene a buscar su silla sino a ver cómo va la cosa.
 *
 * Carga junta lo que es chico (el torneo, la tabla, las rondas) igual que la
 * pantalla del jugador, y deja las mesas para cuando se miran.
 */

const PESTANAS: readonly { id: PestanaPublica; texto: string }[] = [
  { id: 'resumen', texto: 'Resumen' },
  { id: 'mesas', texto: 'Mesas' },
  { id: 'posiciones', texto: 'Posiciones' },
]

interface Datos {
  torneo: TorneoPublico | null
  posiciones: PosicionPublica[]
  equipos: PosicionEquipoPublica[]
  rondas: RondaPublica[]
}

/**
 * Con `conSesion`, antes de mostrarlo pregunta si el torneo es del jugador: si
 * lo juega, lo manda a su propia pantalla del torneo. Mirar la propia mesa como
 * espectador confunde (no dice cuál es la suya ni deja cargar las manos), y al
 * torneo se llega igual desde la vitrina o desde un enlace que le pasaron.
 */
export function VitrinaTorneo({
  torneoId,
  pestana,
  conSesion = false,
}: {
  torneoId: string
  pestana: PestanaPublica
  conSesion?: boolean
}) {
  const [vuelta, setVuelta] = useState(0)
  /* En una ref y no en las dependencias: cambiar de pestaña no tiene que volver
     a pedir el torneo. */
  const pestanaPedida = useRef(pestana)
  pestanaPedida.current = pestana

  const pedir = useCallback(async (): Promise<Datos | null> => {
    const repo = await obtenerRepositorio()
    /* Las dos preguntas van juntas para no sumar un viaje al que solo mira. Si
       la del torneo propio falla, se muestra la vitrina: es lo que pidió. */
    const [torneo, propio] = await Promise.all([
      repo.obtenerTorneoPublico(torneoId),
      conSesion ? repo.obtenerTorneo(torneoId).catch(() => null) : null,
    ])
    if (propio) {
      reemplazar(vistaPropia(torneoId, pestanaPedida.current))
      return null
    }
    if (!torneo) return { torneo: null, posiciones: [], equipos: [], rondas: [] }
    const [posiciones, equipos, rondas] = await Promise.all([
      repo.posicionesPublicas(torneoId),
      torneo.modalidad === 'equipos' ? repo.posicionesEquiposPublicas(torneoId) : [],
      repo.rondasPublicas(torneoId),
    ])
    return { torneo, posiciones, equipos, rondas }
  }, [torneoId, conSesion])

  const { datos, error, cargando, recargar, refrescar } = useCarga(pedir)

  /* La tabla y las rondas cambian cuando se arma o se cierra una ronda. Las
     manos de cada mesa no las tocan: esas las sigue la pestaña «Mesas». */
  const pronto = useLlamadaEspaciada(refrescar, 3_000)
  useAvisosDelTorneo(torneoId, true, (aviso) => {
    if (aviso.tipo !== 'mesa') pronto()
  })

  const actualizar = useCallback(() => {
    setVuelta((n) => n + 1)
    recargar()
  }, [recargar])

  if (error) return <Problema mensaje={error} alReintentar={recargar} />
  /* `null` es que se está yendo a la pantalla del torneo propio. */
  if (!datos) return <Cargando que="Abriendo el torneo…" />

  const { torneo, posiciones, equipos, rondas } = datos

  if (!torneo) {
    return (
      <div className="pantalla">
        <Vacio titulo="Ese torneo no está publicado">
          <p>O no existe, o el club todavía no lo abrió al público.</p>
          <button className="boton secundario" onClick={() => reemplazar({ nombre: 'vivo' })}>
            Ver los torneos en vivo
          </button>
        </Vacio>
      </div>
    )
  }

  const a = (p: PestanaPublica) => ir({ nombre: 'vivo-torneo', id: torneo.id, pestana: p })

  return (
    <div className="pantalla pantalla-torneo">
      <button className="volver" onClick={() => ir({ nombre: 'vivo' })}>
        <Icono nombre="atras" tamano={18} />
        En vivo
      </button>

      <header className="torneo-cabecera">
        <p className="torneo-club">{torneo.organizacion}</p>
        <div className="torneo-cabecera-titulo">
          <h1>{torneo.nombre}</h1>
          <PastillaVitrina estado={torneo.estado} />
        </div>

        <p className="torneo-meta">
          <span>
            <Icono nombre="calendario" tamano={15} />
            {fechaLarga(torneo.fecha)}
          </span>
          {torneo.sede && (
            <span>
              <Icono nombre="sede" tamano={15} />
              {torneo.sede}
            </span>
          )}
          <span>
            <Icono nombre="gente" tamano={15} />
            {cuantosCompiten(torneo.participantes, torneo.modalidad)}
          </span>
        </p>

        {torneo.partidasAGanar !== null ? (
          <MarcadorMatch
            partidasAGanar={torneo.partidasAGanar}
            posiciones={posiciones}
            terminado={torneo.estado === 'finalizado'}
          />
        ) : (
          torneo.estado !== 'inscripcion' && <Avance torneo={torneo} />
        )}
      </header>

      <nav className="pestanas" aria-label="Secciones del torneo">
        {PESTANAS.map((p) => (
          <button
            key={p.id}
            className={pestana === p.id ? 'activa' : ''}
            aria-current={pestana === p.id ? 'page' : undefined}
            onClick={() => a(p.id)}
          >
            {p.texto}
          </button>
        ))}
      </nav>

      {pestana === 'resumen' && (
        <Resumen
          torneo={torneo}
          posiciones={posiciones}
          rondas={rondas}
          vuelta={vuelta}
          alActualizar={actualizar}
          alVer={a}
        />
      )}

      {pestana === 'mesas' && <MesasDeRonda torneoId={torneo.id} rondas={rondas} publico />}

      {pestana === 'posiciones' &&
        (posiciones.length === 0 ? (
          <Vacio titulo="Todavía no hay tabla">
            <p>La tabla se arma al iniciar el torneo, con los que hicieron check-in.</p>
          </Vacio>
        ) : (
          <TablaPosiciones
            posiciones={posiciones}
            equipos={equipos}
            hayPodio={torneo.rondasCerradas > 0}
          />
        ))}
    </div>
  )
}

function Resumen({
  torneo,
  posiciones,
  rondas,
  vuelta,
  alActualizar,
  alVer,
}: {
  torneo: TorneoPublico
  posiciones: PosicionPublica[]
  rondas: RondaPublica[]
  vuelta: number
  alActualizar: () => void
  alVer: (p: PestanaPublica) => void
}) {
  return (
    <div className="resumen">
      {torneo.estado === 'inscripcion' && (
        <Vacio titulo="Todavía no empezó">
          <p>
            Se juega el {fechaLarga(torneo.fecha)}. Las mesas y la tabla aparecen acá en cuanto se
            arme la primera ronda.
          </p>
        </Vacio>
      )}

      {torneo.estado === 'en_curso' && (
        <MesasDestacadas
          key={vuelta}
          torneoId={torneo.id}
          rondas={rondas}
          alVerTodas={() => alVer('mesas')}
          alActualizar={alActualizar}
        />
      )}

      {torneo.estado === 'finalizado' && posiciones.length > 0 && (
        <section className="seccion">
          <h3 className="seccion-titulo">El podio</h3>
          <PodioFinal posiciones={posiciones} />
        </section>
      )}

      {torneo.rondasCerradas > 0 && (
        <Punteros
          posiciones={torneo.estado === 'finalizado' ? posiciones.slice(3) : posiciones}
          titulo={torneo.estado === 'finalizado' ? 'Los que siguen' : 'Van adelante'}
          alVerTabla={() => alVer('posiciones')}
        />
      )}

      <section className="seccion">
        <h3 className="seccion-titulo">El torneo</h3>
        <dl className="resumen-datos">
          <div>
            <dt>Modalidad</dt>
            <dd>{MODALIDAD[torneo.modalidad] ?? torneo.modalidad}</dd>
          </div>
          <div>
            <dt>Se juega a</dt>
            <dd>{torneo.objetivoPuntos} puntos</dd>
          </div>
          {torneo.partidasAGanar !== null ? (
            <div>
              <dt>Partidas a ganar</dt>
              <dd>{torneo.partidasAGanar}</dd>
            </div>
          ) : (
            <div>
              <dt>Rondas</dt>
              <dd>{torneo.numeroRondas}</dd>
            </div>
          )}
          <div>
            <dt>
              {torneo.modalidad === 'parejas'
                ? 'Parejas'
                : torneo.estado === 'inscripcion'
                  ? 'Anotados'
                  : 'Participantes'}
            </dt>
            <dd>{torneo.participantes}</dd>
          </div>
        </dl>
      </section>
    </div>
  )
}
